// 批次 25（#27，Gitea issue #27）：登入頁公告系統（index.jsp 左欄公告＋indexAnnounce.js）。
//
// legacy 來源與行號（2026-09-28 R1 複驗後全量勘誤，行號以當場 grep -n 親驗）：
//   - WEB-INF/views/index.jsp:69 `<%@ include file="announceModal.jsp" %>`（左欄公告欄＋兩個 modal）。
//   - announceModal.jsp:9-34 公告欄面板（NO./主旨/公告時間＋More 鈕 #showModalButton :31）；
//     :36-66 announceListModal（公告欄 modal：全量清單＋關閉）；
//     :69-125 announcementModal（單則公告：起訖 annoDate／公告單位 annoRefName／公告對象
//     annoObjName／相關網址 url（空→「無」）／公告內容 content（\n→<br>）＋附件表
//     檔案名稱(:105)/檔案大小(:106)/第 3 欄 `<th>&nbsp;</th>`(:107)）。
//   - js-my/indexAnnounce.js:
//     :7-9 More→announceListModal；:10-21 .showAnnounce（#announceDiv/#announceListDiv 委派
//     data-index/data-id）→showAnnounce；
//     :28-31 queryAnnouncements（呼叫 :30 getAnnouncements('#announceDiv', 6)）；
//     :101-152 getAnnouncements（POST login/getAnnouncementList_newAjax :104；index==0 subject
//     寫 #loginAnncDiv :115-119；announceDiv limit 6；失敗 showAlertDialog(response.desc) :147-149）；
//     :154-161 genAnnouncementsHtml（NO.＝index+1／subject 按鈕／startDateStr）；
//     :162-170 genSubject（length>30 → substring(0,30)+'...'）；
//     :172-203 showAnnounce（annoDate＝startDateStr+' ~ '+endDateStr :174；url 非空→<a target=_blank>
//     否則「無」:178；content \n→<br> :179；check-width 圖片寬度檢查 :181-197）；
//     :207-225 setAttachFileList（POST login/getMyAttachFileListAjax refTable/refId；refId null/0
//     →空清單回呼）＋:34-69 initAnnouncementAttachFileTable（3 欄＋downloadAttachmentBtn :47）/
//     :71-87 showAnnouncementAttachFileTable（list.length==0 → #fileListDiv.hide() :82-86）；
//     :55-59 downloadAttachmentBtn 委派 → downloadFile(row.fileId)。
//   - js-my/index.js:56-73 modal_size 狀態機：清單 modal show→1（:72-73）／單則 show
//     （hide 清單 modal，1→2 :61-65）／單則 hide（2→重開清單 modal :56-59）／清單 modal hide（1→0 :67-70）。
//
// 誠實標註（KNOWN-NOT-DONE D25-x，詳 src/ui/docs/KNOWN-NOT-DONE.md）：
//   - 附件清單＝樣本常數 INDEX_ANNOUNCEMENT_ATTACH_SEED（getMyAttachFileListAjax 未映射
//     endpoint-map＝C5-j 樣本常數 0 data-effect，批次 17 getRecommendTeacher 先例）；
//     raw-dom 未捕獲附件（modal 未開啟）【雛形值待實機校準】。
//   - 附件下載＝Blob 占位（common.downloadFile → /download?fileId 端點未映射；批次 23
//     範本 Blob 先例）【雛形值待實機校準】。
//   - 公告 modal 內 DataTable chrome（wrapper/分頁列）不鏡像＝GNL-003/#31 同族。
//   - ckeditor 圖片寬度檢查（showAnnounce check-width/.widthLimint）不鏡像（種子無圖片）。
//
// 【證據等級】§7 第 3 級（原始碼）。

import type { ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { Modal } from '@/components/base/Modal';
import type { Row } from '@/mocks/types';
import { INDEX_ANNOUNCEMENT_ATTACH_SEED } from '@/mocks/resource-rules';

/** 公告欄頁面清單筆數（indexAnnounce.js:28-31 queryAnnouncements，呼叫 :30 getAnnouncements('#announceDiv', 6)）。 */
const ANNOUNCE_PAGE_LIMIT = 6;

/** 主旨截斷（indexAnnounce.js:162-170 genSubject：length>30 → substring(0,30)+'...'）。 */
export function truncateSubject(subject: string): string {
  if (subject.length > 30) {
    return `${subject.substring(0, 30)}...`;
  }
  return subject;
}

/** 附件清單（樣本常數；getMyAttachFileListAjax 未映射＝D25-x）。 */
export function attachmentListOf(annoId: string): readonly Row[] {
  return INDEX_ANNOUNCEMENT_ATTACH_SEED[annoId] ?? [];
}

/** 附件下載 Blob 占位（indexAnnounce.js:47 downloadAttachmentBtn render＋:55-59 委派 → downloadFile(fileId)；D25-x）。 */
export function downloadAttachmentBlob(fileName: string): void {
  // jsdom（單元測試環境）沒有 URL.createObjectURL → 安全跳過；實體瀏覽器才會真出下載。
  if (typeof URL.createObjectURL !== 'function') {
    return;
  }
  const blob = new Blob(
    ['React 雛形佔位附件（legacy 來源：common.downloadFile → /download?fileId=…，實機校準後由真後端提供）'],
    { type: 'text/plain' },
  );
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

/** 公告列（NO./主旨/公告時間；indexAnnounce.js:154-161 genAnnouncementsHtml，subject 按鈕 :158）。 */
function AnnounceRow({
  pageId,
  scopeKey,
  index,
  row,
  onOpen,
}: {
  pageId: string;
  scopeKey: string;
  index: number;
  row: Row;
  onOpen: (index: number) => void;
}) {
  return (
    <div className="flex items-center gap-2 border-border border-b py-1 text-sm" data-ui-id={`${pageId}-${scopeKey}-item-${String(index)}`}>
      <span className="w-10 shrink-0 text-right text-ink-muted">{String(index + 1)}</span>
      <span className="flex-1 text-left">
        <Button
          uiId={`${pageId}-${scopeKey}-open-${String(index)}`}
          variant="link"
          size="sm"
          type="button"
          onClick={() => onOpen(index)}
        >
          {truncateSubject(String(row['subject'] ?? ''))}
        </Button>
      </span>
      <span className="w-28 shrink-0 text-ink-muted">{String(row['startDateStr'] ?? '')}</span>
    </div>
  );
}

/** 左欄公告欄面板（announceModal.jsp:8-33：NO./主旨/公告時間＋More）。 */
export function LoginAnnouncementPanel({
  pageId,
  rows,
  onMore,
  onOpen,
}: {
  pageId: string;
  rows: readonly Row[];
  onMore: () => void;
  onOpen: (index: number) => void;
}): ReactElement {
  const visible = rows.slice(0, ANNOUNCE_PAGE_LIMIT);
  return (
    <section data-ui-id={`${pageId}-announce-panel`} className="rounded-[4px] bg-surface/90 p-4 shadow-sm">
      <h4 data-ui-id={`${pageId}-announce-heading`} className="mb-2 font-bold text-[18px]">
        公告欄
      </h4>
      <div className="flex items-center gap-2 border-border border-b pb-1 text-sm font-bold" data-ui-id={`${pageId}-announce-list-head`}>
        <span className="w-10 shrink-0 text-right">NO.</span>
        <span className="flex-1 pl-2">主旨</span>
        <span className="w-28 shrink-0">公告時間</span>
      </div>
      <div data-ui-id={`${pageId}-announce-list`}>
        {visible.map((row, index) => (
          <AnnounceRow key={String(row['id'] ?? index)} pageId={pageId} scopeKey="announce" index={index} row={row} onOpen={onOpen} />
        ))}
      </div>
      <div className="mt-2 text-right">
        {/* More（announceModal.jsp:31 #showModalButton → announceListModal；indexAnnounce.js:7-9）。 */}
        <Button uiId={`${pageId}-announce-more`} variant="primary" size="sm" type="button" onClick={onMore}>
          <strong>More</strong>
        </Button>
      </div>
    </section>
  );
}

/** 公告欄 modal（announceModal.jsp:36-66 announceListModal：全量清單＋關閉）。 */
export function AnnounceListModal({
  pageId,
  rows,
  onOpen,
  onClose,
}: {
  pageId: string;
  rows: readonly Row[];
  onOpen: (index: number) => void;
  onClose: () => void;
}): ReactElement {
  return (
    <Modal
      uiId={`${pageId}-announce-list-modal`}
      open
      title="公告欄"
      onClose={onClose}
      footer={
        <Button uiId={`${pageId}-announce-list-close`} variant="default" size="sm" type="button" onClick={onClose}>
          關閉
        </Button>
      }
    >
      <div className="flex items-center gap-2 border-border border-b pb-1 text-sm font-bold">
        <span className="w-10 shrink-0 text-right">NO.</span>
        <span className="flex-1 pl-2">主旨</span>
        <span className="w-28 shrink-0">公告時間</span>
      </div>
      <div data-ui-id={`${pageId}-announce-list-div`}>
        {rows.map((row, index) => (
          <AnnounceRow key={String(row['id'] ?? index)} pageId={pageId} scopeKey="announce-list" index={index} row={row} onOpen={onOpen} />
        ))}
      </div>
    </Modal>
  );
}

/** 單則公告 modal（announceModal.jsp:69-125 announcementModal＋indexAnnounce.js:172-203 showAnnounce）。 */
export function AnnounceDetailModal({
  pageId,
  row,
  onClose,
}: {
  pageId: string;
  row: Row;
  onClose: () => void;
}): ReactElement {
  const url = String(row['url'] ?? '');
  const attachments = attachmentListOf(String(row['id'] ?? ''));
  return (
    <Modal
      uiId={`${pageId}-announcement-modal`}
      open
      title={String(row['subject'] ?? '')}
      onClose={onClose}
      footer={
        <Button uiId={`${pageId}-announcement-modal-footer-close`} variant="default" size="sm" type="button" onClick={onClose}>
          關閉
        </Button>
      }
    >
      {/* annoDate（showAnnounce：startDateStr+' ~ '+endDateStr）。 */}
      <p className="mb-2 text-sm text-ink-muted" data-ui-id={`${pageId}-announce-date`}>
        {`${String(row['startDateStr'] ?? '')} ~ ${String(row['endDateStr'] ?? '')}`}
      </p>
      <div className="mb-1 flex gap-2 text-sm">
        <span className="w-20 shrink-0 text-right">公告單位：</span>
        <span data-ui-id={`${pageId}-announce-ref-name`}>{String(row['refName'] ?? '')}</span>
      </div>
      <div className="mb-1 flex gap-2 text-sm">
        <span className="w-20 shrink-0 text-right">公告對象：</span>
        <span data-ui-id={`${pageId}-announce-obj-name`}>{String(row['roleNames'] ?? '')}</span>
      </div>
      <div className="mb-1 flex gap-2 text-sm">
        <span className="w-20 shrink-0 text-right">相關網址：</span>
        <span data-ui-id={`${pageId}-announce-url`}>
          {/* url 非空 → <a target=_blank>；空 → 「無」（showAnnounce :178）。 */}
          {url === '' ? (
            '無'
          ) : (
            <a href={url} target="_blank" rel="noreferrer">
              {url}
            </a>
          )}
        </span>
      </div>
      <div className="mb-2 flex gap-2 text-sm">
        <span className="w-20 shrink-0 text-right">公告內容：</span>
        <div data-ui-id={`${pageId}-announce-content`} className="whitespace-pre-wrap">
          {/* \n 換行以 pre-wrap 呈現；legacy showAnnounce :179 \n→<br> 的渲染等價，不插字面 <br>。 */}
          {String(row['content'] ?? '')}
        </div>
      </div>
      {/* 附件表（list.length==0 → fileListDiv 隱藏，indexAnnounce.js:82-86；3 欄 init :34-69/下載鈕 :47/:55-59）。 */}
      {attachments.length === 0 ? null : (
        <div data-ui-id={`${pageId}-announce-file-list`}>
          <table className="w-full text-sm" data-ui-id={`${pageId}-announce-attach-table`}>
            <thead>
              <tr className="border-border border-b">
                <th className="px-2 py-1 text-left">檔案名稱</th>
                <th className="px-2 py-1 text-right">檔案大小</th>
                <th className="px-2 py-1">&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {attachments.map((file) => (
                <tr key={String(file['id'])} data-ui-id={`${pageId}-announce-attach-row-${String(file['id'])}`}>
                  <td className="px-2 py-1">{String(file['name'] ?? '')}</td>
                  <td className="px-2 py-1 text-right">{String(file['size'] ?? '')}</td>
                  <td className="px-2 py-1 text-right">
                    <Button
                      uiId={`${pageId}-announce-attach-download-${String(file['fileId'] ?? file['id'])}`}
                      variant="primary"
                      size="sm"
                      type="button"
                      onClick={() => downloadAttachmentBlob(String(file['name'] ?? 'attachment'))}
                    >
                      下載
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
